/* Brand registry (cascade plan 3.3), ported from d2d-UEM's src/brand.ts (same
 * design as d2d-XDR's).
 *
 * One entry per preset. The colours are NOT here: they are CSS (d2d is the
 * base, in config.css / tokens.css). This file holds what CSS cannot: the
 * name, the copy, the image paths. Every place the product names itself reads
 * BRAND.name; the agent's own on-disk names are src/lib/agentNames.ts.
 *
 * DLP ships two presets, d2d (the base) and altide (brand plan, same as d2d-XDR,
 * d2d-UEM and d2d-DetectHub). A further brand is a new entry here, a
 * src/styles/brands/<id>.css preset and its images in public/brands/<id>/;
 * brand.check.ts then requires all three.
 *
 * The brand is fixed at build time by VITE_BRAND, which Vite inlines.
 * index.html gets the same brand from vite.config.ts's brand-html plugin, so
 * the shell and the app always agree.
 */

export type BrandId = "d2d" | "altide";

export interface Brand {
  id: BrandId;
  /** Product name as users see it: tab title, login, sidebar, copy. */
  name: string;
  tagline: string;
  /** The vendor, as in "© 2026 d2d". */
  company: string;
  /** Logo mark: sidebar, sign-in and first-run setup. */
  mark: string;
  favicon: string;
  /** Home-screen and touch icon. */
  icon: string;
}

export const BRANDS: Readonly<Record<BrandId, Brand>> = {
  d2d: {
    id: "d2d",
    name: "d2d-DLP",
    tagline: "Data Loss Prevention",
    company: "d2d",
    mark: "/brands/d2d/mark.png",
    favicon: "/brands/d2d/favicon.ico",
    icon: "/brands/d2d/icon.png",
  },
  altide: {
    id: "altide",
    name: "Altide DLP",
    tagline: "Data Loss Prevention",
    company: "Altide",
    mark: "/brands/altide/mark.png",
    favicon: "/brands/altide/favicon.ico",
    icon: "/brands/altide/icon.png",
  },
};

/** An unknown or missing id falls back to d2d, the base, never to nothing. */
export function resolveBrand(id: string | undefined): Brand {
  return BRANDS[(id ?? "") as BrandId] ?? BRANDS.d2d;
}

// import.meta.env is undefined when vite.config.ts imports this file in Node,
// hence the optional chain; the config passes its own env to resolveBrand().
export const BRAND: Brand = resolveBrand(import.meta.env?.VITE_BRAND);

// Returns the build-time constant; a per-tenant brand would turn this into a
// context read with the same signature, so callers would not change.
export function useBrand(): Brand {
  return BRAND;
}
